import { addDays, subDays, format } from 'date-fns';
import type {
  CollectionCase,
  PromiseToPay,
  CollectionActivity,
  TeamMemberPerformance,
  CollectionAlert,
} from '@/types/collections';

// Generate mock collection cases
export function generateMockCases(): CollectionCase[] {
  // Customer names from mock-customers.ts (56 customers)
  const customers = [
    'Acme Corporation', 'Global Tech Industries', 'Mega Retail Group', 'Premier Manufacturing',
    'Sunrise Enterprises', 'Quantum Solutions', 'Atlantic Trading Co', 'Pacific Imports',
    'Northern Logistics', 'Summit Distribution', 'Coastal Ventures', 'Metro Services Group',
    'Horizon Technologies', 'Silverline Industries', 'Apex Commercial', 'Pinnacle Systems',
    'Vertex Corporation', 'Fusion Enterprises', 'Nexus Trading', 'Catalyst Solutions',
    'Zenith Group', 'Vanguard Industries', 'Omega Systems', 'Titan Commercial',
    'Eclipse Trading', 'Meridian Group', 'Spectrum Solutions', 'Prism Enterprises',
    'Cascade Industries', 'Stellar Corporation', 'Quantum Dynamics', 'Nexus Ventures',
    'Bright Future Inc', 'Swift Solutions', 'Reliable Trading Co', 'Premier Partners',
    'Excellence Corp', 'Integrity Systems', 'Quality Enterprises', 'Trustworthy Trading',
    'Precision Industries', 'Efficient Solutions', 'Dependable Corp', 'Solid Ventures',
    'Stable Systems', 'Consistent Trading', 'Steady Enterprises', 'Secure Solutions',
    'Prompt Partners', 'Swift Industries', 'Rapid Response Co', 'Timely Trading',
    'Punctual Systems', 'Expedite Enterprises', 'Agile Corporation', 'Dynamic Solutions',
  ];

  // Contact names for each customer
  const contactNames = [
    'John Smith', 'Sarah Mitchell', 'David Chen', 'Robert Williams',
    'Emily Rodriguez', 'James Patterson', 'Amanda Foster', 'Thomas Anderson',
    'Michael Thompson', 'Jennifer Davis', 'Christopher Lee', 'Patricia Martinez',
    'Daniel Taylor', 'Linda Anderson', 'Matthew Thomas', 'Barbara Jackson',
    'Anthony White', 'Susan Harris', 'Mark Martin', 'Jessica Thompson',
    'Paul Garcia', 'Nancy Martinez', 'Steven Robinson', 'Karen Clark',
    'Andrew Rodriguez', 'Betty Lewis', 'Joshua Lee', 'Helen Walker',
    'Kenneth Hall', 'Dorothy Allen', 'Kevin Young', 'Sandra Hernandez',
    'Brian King', 'Ashley Wright', 'George Lopez', 'Kimberly Hill',
    'Edward Scott', 'Donna Green', 'Ronald Adams', 'Carol Baker',
    'Timothy Nelson', 'Michelle Carter', 'Jason Mitchell', 'Sarah Perez',
    'Jeffrey Roberts', 'Lisa Turner', 'Ryan Phillips', 'Laura Campbell',
    'Jacob Parker', 'Amy Evans', 'Gary Edwards', 'Stephanie Collins',
    'Nicholas Stewart', 'Rebecca Sanchez', 'Eric Morris', 'Deborah Rogers',
  ];

  const priorities: Array<'High' | 'Medium' | 'Low'> = ['High', 'Medium', 'Low'];
  const statuses: Array<'Active' | 'On Hold' | 'Closed' | 'Promised' | 'Escalated'> = ['Active', 'On Hold', 'Closed', 'Promised', 'Escalated'];
  const riskLevels: Array<'High' | 'Medium' | 'Low'> = ['High', 'Medium', 'Low'];
  
  // Force some specific cases to have broken promises for testing
  const forceBrokenPromiseCases = [2, 5, 8, 12, 15, 20, 25]; // These case indices will have broken promises

  const nextActions = [
    'Send payment reminder email',
    'Schedule follow-up call',
    'Escalate to manager',
    'Send final notice',
    'Arrange payment plan',
    'Request updated contact info',
    'Send account statement',
    'Schedule payment discussion',
  ];

  const assignedUsers = [
    'Sarah Johnson',
    'Mike Chen',
    'Emily Rodriguez',
    'David Kim',
    'Lisa Anderson',
    'James Wilson',
    'Maria Garcia',
  ];

  const phaseNames = [
    'Initial Contact',
    'Follow-up & Negotiation',
    'Payment Arrangement',
    'Final Notice',
    'Escalation',
  ];

  return customers.map((customer, index) => {
    const daysInCollection = Math.floor(Math.random() * 90) + 1;
    // Force first 30 cases to be due today (nextActionDays = 0), rest are random
    const nextActionDays = index < 30 ? 0 : Math.floor(Math.random() * 7) - 2; // -2 to 5 days
    const priority = priorities[Math.floor(Math.random() * priorities.length)];
    
    // Force specific cases to be Active with broken promises
    const hasBrokenPromise = forceBrokenPromiseCases.includes(index);
    // Force first 30 cases to be Active so they show up in "Cases Due Today"
    const status = (index < 30 || hasBrokenPromise) ? 'Active' : statuses[Math.floor(Math.random() * statuses.length)];

    const strategyType: Array<'Standard' | 'Intensive' | 'VIP' | 'High Risk'> = ['Standard', 'Intensive', 'VIP', 'High Risk'];
    
    // All cases must have an assigned user
    const assignedTo = assignedUsers[Math.floor(Math.random() * assignedUsers.length)];
    const assignedUserIndex = Math.floor(Math.random() * assignedUsers.length) + 1;
    
    // Generate strategy phase based on days in collection and status
    const totalPhases = Math.floor(Math.random() * 2) + 4; // 4-5 phases
    let currentPhase: number;
    
    if (status === 'Closed') {
      currentPhase = totalPhases; // Completed all phases
    } else if (daysInCollection < 15) {
      currentPhase = 1;
    } else if (daysInCollection < 30) {
      currentPhase = 2;
    } else if (daysInCollection < 45) {
      currentPhase = 3;
    } else if (daysInCollection < 60) {
      currentPhase = 4;
    } else {
      currentPhase = Math.min(totalPhases, 5);
    }
    
    // Generate promise data if status is "Promised" or if case has broken promises
    let promiseCount: number;
    let brokenPromiseCount: number;
    
    if (hasBrokenPromise) {
      // Cases with broken promises should have 1-3 broken promises
      promiseCount = Math.floor(Math.random() * 3) + 1; // 1-3 promises
      brokenPromiseCount = Math.floor(Math.random() * promiseCount) + 1; // At least 1 broken
    } else {
      promiseCount = Math.floor(Math.random() * 3); // 0-2 previous promises
      brokenPromiseCount = Math.floor(Math.random() * promiseCount);
    }
    
    let currentPromise: PromiseToPay | undefined;
    let promiseHistory: PromiseToPay[] | undefined;
    
    if (status === 'Promised') {
      const promiseDaysOffset = Math.floor(Math.random() * 14) + 1; // 1-14 days from now
      const promiseDate = addDays(new Date(), promiseDaysOffset);
      const promisedAmount = Math.floor(Math.random() * 20000) + 5000;
      
      currentPromise = {
        id: `PRM-${String(index + 1).padStart(5, '0')}`,
        customerId: String(index + 1),
        customerName: customer,
        customerAccountNumber: `ACC-${String(index + 1).padStart(3, '0')}`,
        customerEmail: `contact@${customer.toLowerCase().replace(/\s+/g, '')}.com`,
        customerPhone: `+1 (555) ${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
        caseId: `case-${index + 1}`,
        caseNumber: `AR-${String(index + 1).padStart(5, '0')}`,
        promiseDate,
        promisedAmount,
        status: 'Active',
        confirmationMethod: ['Phone', 'Email', 'Portal'][Math.floor(Math.random() * 3)] as 'Phone' | 'Email' | 'Portal',
        createdBy: assignedTo || assignedUsers[Math.floor(Math.random() * assignedUsers.length)],
        createdAt: subDays(new Date(), Math.floor(Math.random() * 5) + 1),
        notes: 'Customer confirmed payment commitment',
        invoices: [{
          id: `inv-${index}-1`,
          headerReference: `INV-${String(index * 10 + 1).padStart(6, '0')}`,
          invoiceDate: subDays(new Date(), Math.floor(Math.random() * 90) + 30),
          originalAmount: promisedAmount,
          promisedAmount,
          currentStatus: 'Unpaid',
          balanceRemaining: promisedAmount,
          transactionDate: subDays(new Date(), Math.floor(Math.random() * 90) + 30),
          dueDate: addDays(new Date(), Math.floor(Math.random() * 30)),
          transactionType: 'Invoice',
          outstandingAmount: promisedAmount,
          currency: 'GBP',
          description: `Invoice for ${customer}`,
          accountCode: null,
          netAmount: null,
          vatAmount: null,
          subLedger: null,
          orderNumber: null,
          createdBy: null,
          modifiedBy: null,
          onDispute: false,
          customerName: customer,
        }],
        isAtRisk: Math.random() > 0.7,
        brokenPromisesCount: brokenPromiseCount,
        assignedUserId: assignedUserIndex,
        assignedUserName: assignedTo,
      };
      
      // Generate promise history if there were previous promises
      if (promiseCount > 0) {
        promiseHistory = Array.from({ length: promiseCount }, (_, i) => ({
          id: `PRM-${String(index + 1).padStart(5, '0')}-H${i + 1}`,
          customerId: String(index + 1),
          customerName: customer,
          customerAccountNumber: `ACC-${String(index + 1).padStart(3, '0')}`,
          customerEmail: `contact@${customer.toLowerCase().replace(/\s+/g, '')}.com`,
          customerPhone: `+1 (555) ${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
          caseId: `case-${index + 1}`,
          caseNumber: `AR-${String(index + 1).padStart(5, '0')}`,
          promiseDate: subDays(new Date(), Math.floor(Math.random() * 30) + 15),
          promisedAmount: Math.floor(Math.random() * 15000) + 3000,
          status: (i < brokenPromiseCount ? 'Broken' : 'Paid') as 'Broken' | 'Paid',
          confirmationMethod: ['Phone', 'Email', 'Portal'][Math.floor(Math.random() * 3)] as 'Phone' | 'Email' | 'Portal',
          createdBy: assignedTo || assignedUsers[Math.floor(Math.random() * assignedUsers.length)],
          createdAt: subDays(new Date(), Math.floor(Math.random() * 60) + 30),
          notes: i < brokenPromiseCount ? 'Promise was not kept' : 'Promise fulfilled',
          invoices: [],
          isAtRisk: false,
          brokenPromisesCount: 0,
          assignedUserId: assignedUserIndex,
          assignedUserName: assignedTo,
        }));
      }
    }
    
    // For cases with broken promises (but not currently "Promised"), generate promise history
    if (hasBrokenPromise && status !== 'Promised' && promiseCount > 0) {
      promiseHistory = Array.from({ length: promiseCount }, (_, i) => ({
        id: `PRM-${String(index + 1).padStart(5, '0')}-H${i + 1}`,
        customerId: String(index + 1),
        customerName: customer,
        customerAccountNumber: `ACC-${String(index + 1).padStart(3, '0')}`,
        customerEmail: `contact@${customer.toLowerCase().replace(/\s+/g, '')}.com`,
        customerPhone: `+1 (555) ${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
        caseId: `case-${index + 1}`,
        caseNumber: `AR-${String(index + 1).padStart(5, '0')}`,
        promiseDate: subDays(new Date(), Math.floor(Math.random() * 30) + 5), // Recent broken promises
        promisedAmount: Math.floor(Math.random() * 15000) + 3000,
        status: 'Broken',
        confirmationMethod: ['Phone', 'Email', 'Portal'][Math.floor(Math.random() * 3)] as 'Phone' | 'Email' | 'Portal',
        createdBy: assignedTo || assignedUsers[Math.floor(Math.random() * assignedUsers.length)],
        createdAt: subDays(new Date(), Math.floor(Math.random() * 45) + 10),
        notes: 'Customer failed to honor payment commitment',
        invoices: [],
        isAtRisk: true,
        brokenPromisesCount: 0,
        assignedUserId: assignedUserIndex,
        assignedUserName: assignedTo,
      }));
    }
    
    return {
      id: `case-${index + 1}`,
      caseId: `AR-${String(index + 1).padStart(5, '0')}`,
      customerId: String(index + 1), // Match customer IDs from mock-customers.ts
      customerName: customer,
      contactName: contactNames[index],
      customerEmail: `contact@${customer.toLowerCase().replace(/\s+/g, '')}.com`,
      customerPhone: `+1 (555) ${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
      priority,
      status,
      strategy: {
        id: `strategy-${Math.floor(Math.random() * 4) + 1}`,
        name: ['Standard Collection', 'Intensive Follow-up', 'VIP Treatment', 'High Risk Protocol'][Math.floor(Math.random() * 4)],
        type: strategyType[Math.floor(Math.random() * strategyType.length)],
      },
      strategyPhase: {
        currentPhase,
        totalPhases,
        phaseName: phaseNames[Math.min(currentPhase - 1, phaseNames.length - 1)],
      },
      amount: Math.floor(Math.random() * 50000) + 2000,
      daysInCollection,
      nextAction: status === 'Promised' ? 'Follow up on promise' : nextActions[Math.floor(Math.random() * nextActions.length)],
      nextActionDate: status === 'Promised' && currentPromise ? currentPromise.promiseDate : addDays(new Date(), nextActionDays),
      riskLevel: riskLevels[Math.floor(Math.random() * riskLevels.length)],
      createdAt: subDays(new Date(), daysInCollection),
      updatedAt: subDays(new Date(), Math.floor(Math.random() * 5)),
      isPaused: Math.random() > 0.9,
      assignedTo,
      currentPromise,
      promiseHistory,
      promiseCount: status === 'Promised' ? (promiseCount + 1) : promiseCount,
      brokenPromiseCount,
      notes: index % 3 === 0 
        ? `Customer contacted on ${format(subDays(new Date(), Math.floor(Math.random() * 5) + 1), 'MMM d, yyyy')}. Discussed payment options and agreed to follow up next week.\n\nCustomer mentioned cash flow issues due to delayed client payments. Requested payment plan with 3 installments.`
        : index % 3 === 1
        ? `Initial contact made. Customer acknowledged the outstanding balance and promised to review invoices.\n\nFollow-up scheduled for ${format(addDays(new Date(), Math.floor(Math.random() * 7) + 1), 'MMM d, yyyy')}.`
        : undefined,
    };
  });
}

// Generate mock promises to pay
export function generateMockPromises(): PromiseToPay[] {
  // Customer names from mock-customers.ts (first 10 customers for promises)
  const customers = [
    { name: 'Acme Corporation', account: 'ACC-001', email: 'john.doe@acme.com', phone: '+1 (555) 123-4567', contactName: 'John Doe' },
    { name: 'Global Tech Industries', account: 'ACC-002', email: 'sarah.smith@globaltech.com', phone: '+1 (555) 234-5678', contactName: 'Sarah Smith' },
    { name: 'Mega Retail Group', account: 'ACC-003', email: 'michael.brown@megaretail.eu', phone: '+44 20 7123 4567', contactName: 'Michael Brown' },
    { name: 'Premier Manufacturing', account: 'ACC-004', email: 'lisa.johnson@premier.com', phone: '+1 (555) 345-6789', contactName: 'Lisa Johnson' },
    { name: 'Sunrise Enterprises', account: 'ACC-005', email: 'david.wilson@sunrise.co.uk', phone: '+44 20 8123 4567', contactName: 'David Wilson' },
    { name: 'Quantum Solutions', account: 'ACC-006', email: 'contact@quantumsolutions.com', phone: '+1 (555) 456-7890', contactName: 'Robert Martinez' },
    { name: 'Atlantic Trading Co', account: 'ACC-007', email: 'contact@atlantictrading.com', phone: '+1 (555) 567-8901', contactName: 'Jennifer Taylor' },
    { name: 'Pacific Imports', account: 'ACC-008', email: 'contact@pacificimports.com', phone: '+1 (555) 678-9012', contactName: 'Thomas Anderson' },
    { name: 'Northern Logistics', account: 'ACC-009', email: 'contact@northernlogistics.com', phone: '+1 (555) 789-0123', contactName: 'Patricia White' },
    { name: 'Summit Distribution', account: 'ACC-010', email: 'contact@summitdistribution.com', phone: '+1 (555) 890-1234', contactName: 'Christopher Lee' },
  ];

  const statuses: Array<PromiseToPay['status']> = ['Active', 'Broken', 'Paid', 'Partially Paid', 'Closed'];
  const methods: Array<PromiseToPay['confirmationMethod']> = ['Phone', 'Email', 'Portal'];
  const teamMembers = ['Sarah Johnson', 'Mike Chen', 'Emily Rodriguez', 'David Kim', 'Lisa Anderson'];

  const promises: PromiseToPay[] = [];

  // Generate 40 promises
  for (let i = 0; i < 40; i++) {
    const customer = customers[i % customers.length];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const promiseDaysOffset = Math.floor(Math.random() * 30) - 15; // -15 to +15 days
    const promiseDate = addDays(new Date(), promiseDaysOffset);
    const createdDaysAgo = Math.floor(Math.random() * 30) + 1;
    const brokenCount = Math.floor(Math.random() * 3);
    
    // 70% of promises are linked to a collection case
    const hasCase = Math.random() > 0.3;
    const caseIndex = Math.floor(Math.random() * 56) + 1; // 56 cases available (matching 56 customers)

    // Generate invoices for this promise
    const invoiceCount = Math.floor(Math.random() * 3) + 1;
    const invoices = Array.from({ length: invoiceCount }, (_, j) => {
      const originalAmount = Math.floor(Math.random() * 15000) + 2000;
      const promisedAmount = Math.floor(originalAmount * (0.5 + Math.random() * 0.5));
      const paidAmount = status === 'Paid' ? promisedAmount : 
                        status === 'Partially Paid' ? Math.floor(promisedAmount * 0.5) : 0;

      return {
        id: `inv-${i}-${j}`,
        headerReference: `INV-${String(i * 10 + j + 1).padStart(6, '0')}`,
        invoiceDate: subDays(new Date(), Math.floor(Math.random() * 90) + 30),
        originalAmount,
        promisedAmount,
        currentStatus: status === 'Paid' ? 'Paid' as const : 
                      status === 'Partially Paid' ? 'Partially Paid' as const : 
                      'Unpaid' as const,
        balanceRemaining: promisedAmount - paidAmount,
        transactionDate: subDays(new Date(), Math.floor(Math.random() * 90) + 30),
        dueDate: addDays(new Date(), Math.floor(Math.random() * 30)),
        transactionType: 'Invoice',
        outstandingAmount: promisedAmount - paidAmount,
        currency: 'GBP',
        description: `Invoice for ${customer.name}`,
        accountCode: null,
        netAmount: null,
        vatAmount: null,
        subLedger: null,
        orderNumber: null,
        createdBy: null,
        modifiedBy: null,
        onDispute: false,
        customerName: customer.name,
      };
    });

    const totalPromised = invoices.reduce((sum, inv) => sum + inv.promisedAmount, 0);

    let daysOverdue: number | undefined;
    if (status === 'Broken') {
      daysOverdue = Math.floor(Math.random() * 20) + 1;
    }

    // Generate last action data
    const actionTypes: Array<'call' | 'email' | 'sms' | 'note' | 'payment' | 'status_change'> = ['call', 'email', 'sms', 'note', 'payment', 'status_change'];
    const actionDescriptions = {
      call: [
        'Follow-up call completed - customer confirmed promise',
        'Attempted contact - left voicemail',
        'Spoke with customer regarding payment plan',
        'Customer requested extension on promise date',
      ],
      email: [
        'Payment reminder email sent',
        'Promise confirmation email delivered',
        'Account statement sent to customer',
        'Follow-up email regarding overdue payment',
      ],
      sms: [
        'SMS reminder sent for upcoming promise date',
        'Payment confirmation SMS delivered',
        'Overdue payment alert sent via SMS',
      ],
      note: [
        'Internal note added regarding customer situation',
        'Updated case notes with payment discussion details',
        'Documented customer\'s financial hardship',
      ],
      payment: [
        'Partial payment received and applied',
        'Full payment received - promise fulfilled',
        'Payment plan installment processed',
      ],
      status_change: [
        'Promise status updated to Broken',
        'Promise status changed to Active',
        'Promise marked as Paid',
      ],
    };

    const actionType = actionTypes[Math.floor(Math.random() * actionTypes.length)];
    const actionDaysAgo = Math.floor(Math.random() * 7) + 1; // Last action within last 7 days
    const actionPerformedBy = teamMembers[Math.floor(Math.random() * teamMembers.length)];
    
    // All promises must have an assigned user
    const assignedUserName = teamMembers[Math.floor(Math.random() * teamMembers.length)];
    const assignedUserId = Math.floor(Math.random() * teamMembers.length) + 1;

    promises.push({
      id: `PRM-${String(i + 1).padStart(5, '0')}`,
      customerId: String((i % customers.length) + 1), // Match customer IDs from mock-customers.ts
      customerName: customer.name,
      customerAccountNumber: customer.account,
      customerEmail: customer.email,
      customerPhone: customer.phone,
      contactName: customer.contactName,
      caseId: hasCase ? `case-${caseIndex}` : '',
      caseNumber: hasCase ? `AR-${String(caseIndex).padStart(5, '0')}` : '',
      promiseDate,
      promisedAmount: totalPromised,
      status,
      confirmationMethod: methods[Math.floor(Math.random() * methods.length)],
      createdBy: teamMembers[Math.floor(Math.random() * teamMembers.length)],
      createdAt: subDays(new Date(), createdDaysAgo),
      notes: [
        'Customer confirmed payment via phone call',
        'Email confirmation sent and acknowledged',
        'Payment plan agreed upon',
        'Customer requested payment extension',
        'Confirmed via customer portal',
      ][Math.floor(Math.random() * 5)],
      invoices,
      daysOverdue,
      isAtRisk: brokenCount > 0 || Math.random() > 0.7,
      brokenPromisesCount: brokenCount,
      assignedUserId,
      assignedUserName,
      lastAction: {
        type: actionType,
        description: actionDescriptions[actionType][Math.floor(Math.random() * actionDescriptions[actionType].length)],
        date: subDays(new Date(), actionDaysAgo),
        performedBy: actionPerformedBy,
      },
    });
  }

  return promises;
}

// Generate mock team performance data
export function generateMockTeamPerformance(): TeamMemberPerformance[] {
  const teamMembers = [
    { name: 'Sarah Johnson', avatar: 'SJ' },
    { name: 'Mike Chen', avatar: 'MC' },
    { name: 'Emily Rodriguez', avatar: 'ER' },
    { name: 'David Kim', avatar: 'DK' },
    { name: 'Lisa Anderson', avatar: 'LA' },
    { name: 'James Wilson', avatar: 'JW' },
    { name: 'Maria Garcia', avatar: 'MG' },
    { name: 'Robert Taylor', avatar: 'RT' },
    { name: 'Jennifer Lee', avatar: 'JL' },
    { name: 'Michael Brown', avatar: 'MB' },
  ];

  return teamMembers.map((member, index) => ({
    id: `team-${index + 1}`,
    name: member.name,
    avatar: member.avatar,
    activeCases: Math.floor(Math.random() * 30) + 5,
    resolvedCases: Math.floor(Math.random() * 50) + 10,
    totalCollected: Math.floor(Math.random() * 500000) + 100000,
    successRate: Math.floor(Math.random() * 30) + 65,
    avgResponseTime: Math.floor(Math.random() * 48) + 12, // hours
    overdueActions: Math.floor(Math.random() * 5),
  }));
}

// Generate mock collection alerts
export function generateMockAlerts(): CollectionAlert[] {
  return [
    {
      id: 'alert-1',
      type: 'urgent',
      title: 'High Priority Cases',
      description: '8 cases require immediate attention',
      actionRequired: true,
      createdAt: subDays(new Date(), 1),
      link: '/collections/queue?priority=high',
    },
    {
      id: 'alert-2',
      type: 'warning',
      title: 'Overdue Accounts Increasing',
      description: '15 accounts moved to 90+ days overdue this week',
      actionRequired: true,
      createdAt: subDays(new Date(), 2),
      link: '/collections/queue?days=90',
    },
    {
      id: 'alert-3',
      type: 'info',
      title: 'Collection Rate Improving',
      description: 'Overall collection rate up 2.5% this month',
      actionRequired: false,
      createdAt: subDays(new Date(), 3),
      link: '/collections/dashboard',
    },
  ];
}

// Generate mock activities
export function generateMockActivities(caseId: string): CollectionActivity[] {
  const activities: CollectionActivity[] = [
    {
      id: 'act-1',
      caseId,
      type: 'email',
      description: 'Payment reminder email sent',
      createdBy: 'Sarah Johnson',
      createdAt: subDays(new Date(), 2),
      outcome: 'Email delivered successfully',
    },
    {
      id: 'act-2',
      caseId,
      type: 'call',
      description: 'Follow-up call made',
      createdBy: 'Sarah Johnson',
      createdAt: subDays(new Date(), 5),
      outcome: 'Customer promised payment by end of week',
    },
    {
      id: 'act-3',
      caseId,
      type: 'note',
      description: 'Case notes updated',
      createdBy: 'Mike Chen',
      createdAt: subDays(new Date(), 7),
      outcome: 'Customer experiencing temporary cash flow issues',
    },
    {
      id: 'act-4',
      caseId,
      type: 'email',
      description: 'Account statement sent',
      createdBy: 'Sarah Johnson',
      createdAt: subDays(new Date(), 10),
      outcome: 'Email opened by recipient',
    },
  ];

  return activities;
}
